import React, { useState, useEffect, useCallback } from 'react';
import ReactQuill from 'react-quill-new';
import 'react-quill-new/dist/quill.snow.css';
import { LogIn, LogOut, Plus, Trash2, Edit3, Save, X, Building2, Award, Upload, ChevronDown, ChevronUp } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const quillModules = {
  toolbar: [
    [{ 'header': [2, 3, false] }],
    ['bold', 'italic', 'underline'],
    [{ 'list': 'ordered' }, { 'list': 'bullet' }],
    ['link'],
    ['clean']
  ]
};

const Admin = () => {
  const [token, setToken] = useState(localStorage.getItem('wfa_admin_token') || '');
  const [loginForm, setLoginForm] = useState({ email: '', password: '' });
  const [loginError, setLoginError] = useState('');
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('companies');
  const [editingCompany, setEditingCompany] = useState(null);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [expandedCompany, setExpandedCompany] = useState(null);
  const [newAwardForm, setNewAwardForm] = useState({ nomination: '', year: '', award_image: '' });
  const [createForm, setCreateForm] = useState({
    name: '', slug: '', title: '', website: '', phone: '', email: '', description: '', legal_name: ''
  });

  const authHeaders = useCallback(() => ({
    'Content-Type': 'application/json',
    'Authorization': `Bearer ${token}`
  }), [token]);

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoginError('');
    try {
      const res = await fetch(`${API_URL}/api/admin/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(loginForm)
      });
      if (!res.ok) throw new Error('Invalid credentials');
      const data = await res.json();
      setToken(data.access_token);
      localStorage.setItem('wfa_admin_token', data.access_token);
    } catch (err) {
      setLoginError(err.message);
    }
  };

  const handleLogout = () => {
    setToken('');
    localStorage.removeItem('wfa_admin_token');
  };

  const fetchCompanies = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/admin/companies`, {
        headers: authHeaders()
      });
      if (res.status === 401) { handleLogout(); return; }
      const data = await res.json();
      setCompanies(data);
    } catch (err) {
      console.error('Failed to fetch companies:', err);
    } finally {
      setLoading(false);
    }
  }, [authHeaders]);

  useEffect(() => {
    if (token) fetchCompanies();
  }, [token, fetchCompanies]);

  const handleCreateCompany = async (e) => {
    e.preventDefault();
    try {
      const slug = createForm.slug || createForm.name.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '');
      const res = await fetch(`${API_URL}/api/admin/companies`, {
        method: 'POST',
        headers: authHeaders(),
        body: JSON.stringify({ ...createForm, slug, awards: [] })
      });
      if (!res.ok) throw new Error('Failed to create');
      setShowCreateForm(false);
      setCreateForm({ name: '', slug: '', title: '', website: '', phone: '', email: '', description: '', legal_name: '' });
      fetchCompanies();
    } catch (err) {
      alert(err.message);
    }
  };

  const handleUpdateCompany = async (slug) => {
    try {
      const res = await fetch(`${API_URL}/api/admin/companies/${slug}`, {
        method: 'PUT',
        headers: authHeaders(),
        body: JSON.stringify(editingCompany)
      });
      if (!res.ok) throw new Error('Failed to update');
      setEditingCompany(null);
      fetchCompanies();
    } catch (err) {
      alert(err.message);
    }
  };

  const handleDeleteCompany = async (slug) => {
    if (!window.confirm(`Delete company "${slug}"? This cannot be undone.`)) return;
    try {
      const res = await fetch(`${API_URL}/api/admin/companies/${slug}`, {
        method: 'DELETE',
        headers: authHeaders()
      });
      if (!res.ok) throw new Error('Failed to delete');
      fetchCompanies();
    } catch (err) {
      alert(err.message);
    }
  };

  const handleAddAward = async (slug) => {
    try {
      const res = await fetch(`${API_URL}/api/admin/companies/${slug}/awards`, {
        method: 'POST',
        headers: authHeaders(),
        body: JSON.stringify(newAwardForm)
      });
      if (!res.ok) throw new Error('Failed to add award');
      setNewAwardForm({ nomination: '', year: '', award_image: '' });
      fetchCompanies();
    } catch (err) {
      alert(err.message);
    }
  };

  const handleDeleteAward = async (slug, index) => {
    if (!window.confirm('Remove this award?')) return;
    try {
      const res = await fetch(`${API_URL}/api/admin/companies/${slug}/awards/${index}`, {
        method: 'DELETE',
        headers: authHeaders()
      });
      if (!res.ok) throw new Error('Failed to remove award');
      fetchCompanies();
    } catch (err) {
      alert(err.message);
    }
  };

  // Login Screen
  if (!token) {
    return (
      <div className="admin-login-page" data-testid="admin-login-page">
        <div className="admin-login-card">
          <div className="admin-login-header">
            <Building2 size={32} />
            <h1>Admin Dashboard</h1>
            <p>World Financial Award Management</p>
          </div>
          <form onSubmit={handleLogin} data-testid="admin-login-form">
            <div className="admin-field">
              <label>Email</label>
              <input
                type="email"
                value={loginForm.email}
                onChange={e => setLoginForm(p => ({ ...p, email: e.target.value }))}
                placeholder="admin@worldfinancialaward.com"
                required
                data-testid="admin-email-input"
              />
            </div>
            <div className="admin-field">
              <label>Password</label>
              <input
                type="password"
                value={loginForm.password}
                onChange={e => setLoginForm(p => ({ ...p, password: e.target.value }))}
                placeholder="Enter password"
                required
                data-testid="admin-password-input"
              />
            </div>
            {loginError && <div className="admin-error" data-testid="admin-login-error">{loginError}</div>}
            <button type="submit" className="admin-btn-primary" data-testid="admin-login-btn">
              <LogIn size={16} /> Sign In
            </button>
          </form>
        </div>
      </div>
    );
  }

  // Dashboard
  return (
    <div className="admin-dashboard" data-testid="admin-dashboard">
      <div className="admin-sidebar">
        <div className="admin-sidebar-brand">
          <Building2 size={20} />
          <span>WFA Admin</span>
        </div>
        <nav className="admin-nav">
          <button
            className={`admin-nav-item ${activeTab === 'companies' ? 'active' : ''}`}
            onClick={() => setActiveTab('companies')}
            data-testid="nav-companies"
          >
            <Building2 size={16} /> Companies
          </button>
          <button
            className={`admin-nav-item ${activeTab === 'stats' ? 'active' : ''}`}
            onClick={() => setActiveTab('stats')}
            data-testid="nav-stats"
          >
            <Award size={16} /> Stats
          </button>
        </nav>
        <button className="admin-logout-btn" onClick={handleLogout} data-testid="admin-logout-btn">
          <LogOut size={16} /> Logout
        </button>
      </div>

      <div className="admin-main">
        <div className="admin-topbar">
          <h2>{activeTab === 'companies' ? 'Companies & Awards' : 'Dashboard Stats'}</h2>
          {activeTab === 'companies' && (
            <button className="admin-btn-primary" onClick={() => setShowCreateForm(true)} data-testid="add-company-btn">
              <Plus size={16} /> Add Company
            </button>
          )}
        </div>

        {activeTab === 'stats' && (
          <div className="admin-stats-grid" data-testid="admin-stats">
            <div className="admin-stat-card">
              <div className="admin-stat-num">{companies.length}</div>
              <div className="admin-stat-label">Total Companies</div>
            </div>
            <div className="admin-stat-card">
              <div className="admin-stat-num">{companies.reduce((acc, c) => acc + (c.awards?.length || 0), 0)}</div>
              <div className="admin-stat-label">Total Awards</div>
            </div>
            <div className="admin-stat-card">
              <div className="admin-stat-num">{[...new Set(companies.flatMap(c => (c.awards || []).map(a => a.year)).filter(Boolean))].length}</div>
              <div className="admin-stat-label">Award Years</div>
            </div>
          </div>
        )}

        {activeTab === 'companies' && (
          <div className="admin-companies-list" data-testid="admin-companies-list">
            {/* Create Company Modal */}
            {showCreateForm && (
              <div className="admin-modal-overlay">
                <div className="admin-modal" data-testid="create-company-modal">
                  <div className="admin-modal-header">
                    <h3>Add New Company</h3>
                    <button onClick={() => setShowCreateForm(false)} className="admin-close-btn"><X size={18} /></button>
                  </div>
                  <form onSubmit={handleCreateCompany} className="admin-form">
                    <div className="admin-form-grid">
                      <div className="admin-field">
                        <label>Company Name *</label>
                        <input value={createForm.name} onChange={e => setCreateForm(p => ({ ...p, name: e.target.value }))} required data-testid="create-name" />
                      </div>
                      <div className="admin-field">
                        <label>Slug (auto-generated if blank)</label>
                        <input value={createForm.slug} onChange={e => setCreateForm(p => ({ ...p, slug: e.target.value }))} data-testid="create-slug" />
                      </div>
                      <div className="admin-field">
                        <label>Type/Title</label>
                        <input value={createForm.title} onChange={e => setCreateForm(p => ({ ...p, title: e.target.value }))} data-testid="create-title" />
                      </div>
                      <div className="admin-field">
                        <label>Website</label>
                        <input value={createForm.website} onChange={e => setCreateForm(p => ({ ...p, website: e.target.value }))} data-testid="create-website" />
                      </div>
                      <div className="admin-field">
                        <label>Phone</label>
                        <input value={createForm.phone} onChange={e => setCreateForm(p => ({ ...p, phone: e.target.value }))} data-testid="create-phone" />
                      </div>
                      <div className="admin-field">
                        <label>Email</label>
                        <input value={createForm.email} onChange={e => setCreateForm(p => ({ ...p, email: e.target.value }))} data-testid="create-email" />
                      </div>
                    </div>
                    <div className="admin-field">
                      <label>Description</label>
                      <div className="admin-quill-wrap" data-testid="create-description">
                        <ReactQuill theme="snow" value={createForm.description} onChange={val => setCreateForm(p => ({ ...p, description: val }))} modules={quillModules} placeholder="Company description..." />
                      </div>
                    </div>
                    <div className="admin-form-actions">
                      <button type="button" className="admin-btn-secondary" onClick={() => setShowCreateForm(false)}>Cancel</button>
                      <button type="submit" className="admin-btn-primary" data-testid="create-submit-btn"><Save size={16} /> Create</button>
                    </div>
                  </form>
                </div>
              </div>
            )}

            {loading ? (
              <div className="admin-loading">Loading companies...</div>
            ) : (
              companies.map((company) => (
                <div className="admin-company-row" key={company.slug} data-testid={`company-row-${company.slug}`}>
                  <div className="admin-company-header" onClick={() => setExpandedCompany(expandedCompany === company.slug ? null : company.slug)}>
                    <div className="admin-company-info">
                      {company.logo && <img src={company.logo} alt="" className="admin-company-thumb" />}
                      <div>
                        <strong>{company.name}</strong>
                        <span className="admin-company-meta">{company.title} | {company.awards?.length || 0} awards</span>
                      </div>
                    </div>
                    <div className="admin-company-actions">
                      <button
                        className="admin-icon-btn edit"
                        onClick={(e) => { e.stopPropagation(); setEditingCompany({ ...company }); }}
                        data-testid={`edit-btn-${company.slug}`}
                      >
                        <Edit3 size={14} />
                      </button>
                      <button
                        className="admin-icon-btn delete"
                        onClick={(e) => { e.stopPropagation(); handleDeleteCompany(company.slug); }}
                        data-testid={`delete-btn-${company.slug}`}
                      >
                        <Trash2 size={14} />
                      </button>
                      {expandedCompany === company.slug ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
                    </div>
                  </div>

                  {expandedCompany === company.slug && (
                    <div className="admin-company-expanded" data-testid={`expanded-${company.slug}`}>
                      <div className="admin-awards-list">
                        <h4><Award size={14} /> Awards ({company.awards?.length || 0})</h4>
                        {(company.awards || []).map((award, idx) => (
                          <div className="admin-award-row" key={idx}>
                            <span className="admin-award-year">{award.year}</span>
                            <span className="admin-award-name">{award.nomination}</span>
                            <button
                              className="admin-icon-btn delete small"
                              onClick={() => handleDeleteAward(company.slug, idx)}
                              data-testid={`delete-award-${company.slug}-${idx}`}
                            >
                              <Trash2 size={12} />
                            </button>
                          </div>
                        ))}
                      </div>
                      <div className="admin-add-award">
                        <h4><Plus size={14} /> Add Award</h4>
                        <div className="admin-award-form">
                          <input placeholder="Nomination" value={newAwardForm.nomination} onChange={e => setNewAwardForm(p => ({ ...p, nomination: e.target.value }))} data-testid={`award-nomination-${company.slug}`} />
                          <input placeholder="Year" value={newAwardForm.year} onChange={e => setNewAwardForm(p => ({ ...p, year: e.target.value }))} data-testid={`award-year-${company.slug}`} />
                          <button className="admin-btn-primary small" onClick={() => handleAddAward(company.slug)} data-testid={`add-award-btn-${company.slug}`}>
                            <Plus size={14} /> Add
                          </button>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              ))
            )}
          </div>
        )}
      </div>

      {/* Edit Company Modal */}
      {editingCompany && (
        <div className="admin-modal-overlay">
          <div className="admin-modal" data-testid="edit-company-modal">
            <div className="admin-modal-header">
              <h3>Edit: {editingCompany.name}</h3>
              <button onClick={() => setEditingCompany(null)} className="admin-close-btn"><X size={18} /></button>
            </div>
            <div className="admin-form">
              <div className="admin-form-grid">
                <div className="admin-field">
                  <label>Name</label>
                  <input value={editingCompany.name} onChange={e => setEditingCompany(p => ({ ...p, name: e.target.value }))} data-testid="edit-name" />
                </div>
                <div className="admin-field">
                  <label>Title</label>
                  <input value={editingCompany.title || ''} onChange={e => setEditingCompany(p => ({ ...p, title: e.target.value }))} data-testid="edit-title" />
                </div>
                <div className="admin-field">
                  <label>Website</label>
                  <input value={editingCompany.website || ''} onChange={e => setEditingCompany(p => ({ ...p, website: e.target.value }))} data-testid="edit-website" />
                </div>
                <div className="admin-field">
                  <label>Phone</label>
                  <input value={editingCompany.phone || ''} onChange={e => setEditingCompany(p => ({ ...p, phone: e.target.value }))} data-testid="edit-phone" />
                </div>
                <div className="admin-field">
                  <label>Email</label>
                  <input value={editingCompany.email || ''} onChange={e => setEditingCompany(p => ({ ...p, email: e.target.value }))} data-testid="edit-email" />
                </div>
              </div>
              <div className="admin-field">
                <label>Description</label>
                <div className="admin-quill-wrap" data-testid="edit-description">
                  <ReactQuill theme="snow" value={editingCompany.description || ''} onChange={val => setEditingCompany(p => ({ ...p, description: val }))} modules={quillModules} placeholder="Company description..." />
                </div>
              </div>
              <div className="admin-form-actions">
                <button className="admin-btn-secondary" onClick={() => setEditingCompany(null)}>Cancel</button>
                <button className="admin-btn-primary" onClick={() => handleUpdateCompany(editingCompany.slug)} data-testid="edit-save-btn">
                  <Save size={16} /> Save Changes
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Admin;
